import { useRef, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, IconButton, Button, Stack, Tooltip, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { StyledTextField, NormalButton } from "../../../components/ui/CommonReusables";
import { StyledDatePicker, datePickerPopperSx } from "../../../components/ui/CalendarPopper";
import RichEditor from "../../../components/rich-editor";
import { dialogCloseSx, primaryButtonSx, cancelButtonSx, subtleIconSx, ghostButtonSx } from "./buttonStyles";
import { isEmptyHtml, EMPTY_HTML } from "../utils/html";

const FieldLabel = ({ children, required }) => (
	<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
		{children}
		{required && (
			<Box component="span" sx={{ color: "error.main" }}>
				{" *"}
			</Box>
		)}
	</Typography>
);

/**
 * One training item inside the curriculum. `draft.key` is a client-only handle that keeps the
 * rich editor mounted across re-renders and removals — the real id only exists on saved rows.
 */
const TrainingFields = ({ draft, index, error, onChange, onRemove, removable }) => (
	<Box sx={{ border: "1px solid #D6E6F5", borderRadius: 2, backgroundColor: "#FBFDFF", p: { xs: 1.5, sm: 2 } }}>
		<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<SchoolOutlinedIcon fontSize="small" sx={{ color: "#3A7DC9" }} />
				<Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1A3F7A" }}>
					Training {index + 1}
				</Typography>
			</Stack>

			{removable && (
				<Tooltip title="Remove training">
					<IconButton size="small" onClick={onRemove} aria-label={`Remove training ${index + 1}`} sx={subtleIconSx}>
						<DeleteOutlineIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
		</Stack>

		{/* Title and date share a row on wider screens — the date is what the calendar view plots. */}
		<Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5, width: "100%" }}>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<FieldLabel required>Training Title</FieldLabel>
				<StyledTextField
					fullWidth
					size="small"
					placeholder="e.g. Medicare Training Day 1"
					value={draft.title}
					onChange={(event) => onChange({ title: event.target.value })}
					error={Boolean(error?.title)}
					helperText={error?.title}
				/>
			</Box>

			<Box sx={{ width: { xs: "100%", sm: 190 }, flexShrink: 0 }}>
				<FieldLabel required>Date Held</FieldLabel>
				<StyledDatePicker
					value={draft.date}
					onChange={(value) => onChange({ date: value })}
					slotProps={{
						textField: { size: "small", error: Boolean(error?.date), helperText: error?.date, fullWidth: true },
						popper: { sx: datePickerPopperSx },
					}}
				/>
			</Box>
		</Stack>

		<Box>
			<FieldLabel>Training Description</FieldLabel>
			<RichEditor
				value={draft.description}
				onChange={(html) => onChange({ description: html })}
				placeholder="What is covered in this session?"
			/>
		</Box>
	</Box>
);

const CurriculumFormDialog = ({ open, onClose, onSubmit, curriculum, submitting = false }) => {
	const isEdit = Boolean(curriculum);

	// Client-only keys for the training rows; ids from the server are kept separately on each draft.
	const nextKey = useRef(0);
	const makeDraft = (training) => ({
		key: (nextKey.current += 1),
		id: training?.id ?? null,
		title: training?.title ?? "",
		// Dayjs while editing, serialized on submit. New sessions default to today.
		date: training?.date ? dayjs(training.date) : dayjs(),
		description: training?.description || EMPTY_HTML,
	});

	// Seeded once at mount — the parent remounts this dialog per curriculum, so the rich editors
	// always read the right content on their first render.
	const [title, setTitle] = useState(curriculum?.title ?? "");
	const [description, setDescription] = useState(curriculum?.description || EMPTY_HTML);
	const [trainings, setTrainings] = useState(() => (curriculum?.trainings?.length ? curriculum.trainings.map(makeDraft) : [makeDraft()]));
	const [errors, setErrors] = useState({});

	const updateTraining = (key, patch) => {
		setTrainings((prev) => prev.map((draft) => (draft.key === key ? { ...draft, ...patch } : draft)));

		// Clear only the field being edited, so a title fix does not hide a missing date.
		const field = Object.keys(patch)[0];
		setErrors((prev) => ({ ...prev, trainings: { ...prev.trainings, [key]: { ...prev.trainings?.[key], [field]: undefined } } }));
	};

	const addTraining = () => setTrainings((prev) => [...prev, makeDraft()]);

	const removeTraining = (key) => setTrainings((prev) => prev.filter((draft) => draft.key !== key));

	const validate = () => {
		const next = { trainings: {} };

		if (!title.trim()) next.title = "Curriculum title is required";

		trainings.forEach((draft) => {
			const issues = {};

			if (!draft.title.trim()) issues.title = "Training title is required";
			if (!draft.date?.isValid?.()) issues.date = "Pick a date";

			if (Object.keys(issues).length) next.trainings[draft.key] = issues;
		});

		setErrors(next);
		return !next.title && Object.keys(next.trainings).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;

		onSubmit({
			title: title.trim(),
			// An untouched editor still emits markup, so empty descriptions are normalized away.
			description: isEmptyHtml(description) ? "" : description,
			trainings: trainings.map((draft) => ({
				id: draft.id,
				title: draft.title.trim(),
				date: draft.date.format("YYYY-MM-DD"),
				description: isEmptyHtml(draft.description) ? "" : draft.description,
			})),
		});

		// Deliberately left open — the caller closes it once the save actually lands, so a failed
		// request leaves the admin's work on screen rather than discarding it.
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="md"
			slotProps={{
				paper: {
					sx: {
						borderRadius: 3,
						// Elementor lets the dialog paper size to its content, so the width is pinned in both
						// directions. It must stay margin-aware (MUI's own fullWidth rule is
						// calc(100% - 2*margin)), otherwise the paper overflows the viewport on small screens.
						m: { xs: 2, sm: 4 },
						width: { xs: "calc(100% - 32px) !important", sm: "calc(100% - 64px) !important" },
						maxWidth: "820px !important",
					},
				},
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 700 }}>
							{isEdit ? "Edit Curriculum" : "Add Curriculum"}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Name the curriculum, then add a training for every session agents can attend
						</Typography>
					</Box>

					<IconButton onClick={onClose} size="small" aria-label="Close curriculum form" sx={dialogCloseSx}>
						<CloseIcon />
					</IconButton>
				</Stack>
			</DialogTitle>

			<DialogContent dividers sx={{ width: "100%" }}>
				{/* The surrounding theme neutralizes the column flex stretch, so each field block
				    is given an explicit full width instead of relying on align-items: stretch. */}
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack spacing={2.5} sx={{ width: "100%", "& > *": { width: "100%", maxWidth: "100%" } }}>
						<Box>
							<FieldLabel required>Curriculum Title</FieldLabel>
							<StyledTextField
								fullWidth
								size="small"
								placeholder="e.g. Medicare Expertise"
								value={title}
								onChange={(event) => {
									setTitle(event.target.value);
									setErrors((prev) => ({ ...prev, title: undefined }));
								}}
								error={Boolean(errors.title)}
								helperText={errors.title}
							/>
						</Box>

						<Box>
							<FieldLabel>Curriculum Description</FieldLabel>
							<RichEditor value={description} onChange={setDescription} placeholder="What will agents learn from this curriculum?" />
						</Box>

						<Divider />

						<Box>
							<Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
								<Box>
									<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
										Trainings
									</Typography>
									<Typography variant="caption" color="text.secondary">
										Each training is a session agents get marked as attended for
									</Typography>
								</Box>

								<Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, flexShrink: 0 }}>
									{trainings.length} added
								</Typography>
							</Stack>

							<Stack spacing={2}>
								{trainings.map((draft, index) => (
									<TrainingFields
										key={draft.key}
										draft={draft}
										index={index}
										error={errors.trainings?.[draft.key]}
										onChange={(patch) => updateTraining(draft.key, patch)}
										onRemove={() => removeTraining(draft.key)}
										removable={trainings.length > 1}
									/>
								))}
							</Stack>

							<Button fullWidth startIcon={<AddIcon />} onClick={addTraining} sx={{ mt: 2, py: 1.25, ...ghostButtonSx }}>
								Add Training
							</Button>
						</Box>
					</Stack>
				</LocalizationProvider>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={submitting} sx={cancelButtonSx}>
					Cancel
				</Button>
				<NormalButton onClick={handleSubmit} disabled={submitting} sx={primaryButtonSx}>
					{submitting ? "Saving..." : isEdit ? "Save Changes" : "Add Curriculum"}
				</NormalButton>
			</DialogActions>
		</Dialog>
	);
};

export default CurriculumFormDialog;
